import {
  normalizeVector,
  rankPapers,
  type EmbeddedPaper,
  type EmbeddedProfileItem,
  type WeightedEmbedding,
} from './ranking';
import type { Paper, ProfileItem, RankingResult } from './types';

export const DEFAULT_SIMILARITY_LIMIT = 20;
export const MAX_SIMILARITY_LIMIT = 100;

export interface ProjectionInput {
  id: string;
  embedding: readonly number[];
}

export interface ProjectionPoint {
  id: string;
  x: number;
  y: number;
}

export interface PublicationClusterOptions {
  profileItems: readonly ProfileItem[];
  selectedPublicationIds: readonly string[];
  profileEmbeddings: Readonly<Record<string, readonly number[]>>;
  profileBodyEmbeddings?: Readonly<Record<string, readonly WeightedEmbedding[]>>;
  papers: readonly Paper[];
  paperEmbeddings: Readonly<Record<string, readonly number[]>>;
  limit: number;
}

export function normalizeSimilarityLimit(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SIMILARITY_LIMIT;
  return Math.max(1, Math.min(MAX_SIMILARITY_LIMIT, Math.floor(value)));
}

export function rankByPublicationCluster(options: PublicationClusterOptions): RankingResult[] {
  const selectedIds = new Set(options.selectedPublicationIds);
  const profiles: EmbeddedProfileItem[] = options.profileItems
    .filter((item) => item.kind === 'publication' && selectedIds.has(item.id))
    .flatMap((item) => {
      const embedding = options.profileEmbeddings[item.id];
      if (embedding === undefined || embedding.length === 0) return [];
      const bodyEmbeddings = options.profileBodyEmbeddings?.[item.id];
      return bodyEmbeddings
        ? [{ item, embedding, bodyEmbeddings }]
        : [{ item, embedding }];
    });
  if (profiles.length === 0) return [];

  const papersById = new Map(options.papers.map((paper) => [paper.id, paper]));
  const papers: EmbeddedPaper[] = Object.entries(options.paperEmbeddings)
    .map(([paperId, embedding]) => ({ paper: papersById.get(paperId), embedding }))
    .filter(
      (candidate): candidate is EmbeddedPaper =>
        candidate.paper !== undefined && candidate.embedding.length > 0,
    );

  return rankPapers(profiles, papers, { explanationCount: profiles.length }).slice(
    0,
    normalizeSimilarityLimit(options.limit),
  );
}

function dot(left: readonly number[], right: readonly number[]): number {
  let total = 0;
  for (let index = 0; index < left.length; index += 1) {
    total += (left[index] ?? 0) * (right[index] ?? 0);
  }
  return total;
}

function principalDirection(
  centered: readonly (readonly number[])[],
  orthogonalTo?: readonly number[],
): number[] {
  const dimensions = centered[0]?.length ?? 0;
  let direction = normalizeVector(
    Array.from({ length: dimensions }, (_, index) => Math.sin((index + 1) * 1.61803398875)),
  );

  for (let iteration = 0; iteration < 40; iteration += 1) {
    const next = new Array<number>(dimensions).fill(0);
    for (const row of centered) {
      const scale = dot(row, direction);
      for (let dimension = 0; dimension < dimensions; dimension += 1) {
        next[dimension] = (next[dimension] ?? 0) + (row[dimension] ?? 0) * scale;
      }
    }
    if (orthogonalTo) {
      const overlap = dot(next, orthogonalTo);
      for (let dimension = 0; dimension < dimensions; dimension += 1) {
        next[dimension] = (next[dimension] ?? 0) - (orthogonalTo[dimension] ?? 0) * overlap;
      }
    }
    const normalized = normalizeVector(next);
    if (normalized.every((value) => value === 0)) break;
    direction = normalized;
  }

  const anchor = direction.reduce(
    (best, value, index) =>
      Math.abs(value) > Math.abs(direction[best] ?? 0) ? index : best,
    0,
  );
  return (direction[anchor] ?? 0) < 0 ? direction.map((value) => -value) : direction;
}

/**
 * Projects normalized embeddings into two dimensions using deterministic PCA.
 * Returned coordinates are scaled independently to the range [-1, 1].
 */
export function projectEmbeddings2d(inputs: readonly ProjectionInput[]): ProjectionPoint[] {
  if (inputs.length === 0) return [];
  const dimensions = inputs[0]?.embedding.length ?? 0;
  if (
    dimensions === 0 ||
    inputs.some((input) => input.embedding.length !== dimensions)
  ) {
    throw new Error('Projection embeddings must have matching, non-zero dimensions.');
  }

  const normalized = inputs.map((input) => normalizeVector(input.embedding));
  const mean = new Array<number>(dimensions).fill(0);
  for (const vector of normalized) {
    for (let index = 0; index < dimensions; index += 1) {
      mean[index] = (mean[index] ?? 0) + (vector[index] ?? 0) / normalized.length;
    }
  }
  const centered = normalized.map((vector) =>
    vector.map((value, index) => value - (mean[index] ?? 0)),
  );
  const first = principalDirection(centered);
  const second = principalDirection(centered, first);
  const raw = centered.map((vector) => ({ x: dot(vector, first), y: dot(vector, second) }));
  const maxX = Math.max(...raw.map(({ x }) => Math.abs(x)), 1e-12);
  const maxY = Math.max(...raw.map(({ y }) => Math.abs(y)), 1e-12);

  return inputs.map((input, index) => ({
    id: input.id,
    x: (raw[index]?.x ?? 0) / maxX,
    y: (raw[index]?.y ?? 0) / maxY,
  }));
}
